import { apiRequest, useAccount } from "@vojomo-core/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

interface QBRData {
  topic_id: string;
  text: string;
  qbr_scores: Record<
    string,
    {
      owner_name: string;
      scores_list: Record<string, { score: string; rationale: string }>;
      active: boolean;
    }
  >;
}

export const useQBRProvider = () => {
  const [qbrData, setQbrData] = useState<QBRData>(null);
  const [isLoadingQbr, setIsLoadingQbr] = useState<boolean>(true);
  const { vojomoAccount, companyName } = useAccount();
  const [personalScoresList, setPersonalScoresList] =
    useState<Record<string, { score: string; rationale: string }>>(null);
  const [isActive, setIsActive] = useState<boolean>(null);

  const fetchQBRData = async () => {
    setIsLoadingQbr(true);
    const data = await apiRequest<QBRData>({
      subRoute: "roadmap",
      path: `/qbr_scoring_data`,
      method: "GET"
    });

    setQbrData(data);
    setIsLoadingQbr(false);
  };

  useEffect(() => {
    fetchQBRData();
  }, [companyName]);

  useEffect(() => {
    if (qbrData) {
      const personalQbr = qbrData.qbr_scores[vojomoAccount["_id"]];

      if (personalQbr) {
        setPersonalScoresList(personalQbr.scores_list);
        setIsActive(personalQbr.active);
      } else {
        setPersonalScoresList({});
        setIsActive(false);
      }
    }
  }, [qbrData, vojomoAccount]);

  const hasTable = useMemo(() => {
    if (!qbrData?.text) return false;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(qbrData.text, "text/html");
      return !!doc.querySelector("table");
    } catch {
      return false;
    }
  }, [qbrData]);

  const getRowColor = useCallback((scoreValue: string) => {
    const parsed = Number(scoreValue);
    if (!scoreValue || Number.isNaN(parsed)) {
      return "";
    }

    if (parsed >= 2.5) return "#2FB764";
    if (parsed >= 1.875) return "#F3C623";
    if (parsed >= 1.25) return "#D66E2E";
    return "#E34A4A";
  }, []);

  return {
    getRowColor,

    qbrData,

    hasTable,
    isLoadingQbr,

    personalScoresList,
    setPersonalScoresList,

    isActive,
    setIsActive,

    fetchQBRData
  };
};

export const QBRProvider = ({ children }) => {
  const accountProvider = useQBRProvider();

  return (
    <AccountContext.Provider value={accountProvider}>
      {children}
    </AccountContext.Provider>
  );
};

export type QBRProps = ReturnType<typeof useQBRProvider>;
export const AccountContext = createContext<QBRProps>(null);
export const useQBR = () => useContext(AccountContext);
